import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Package, User, Phone, FileText, Trash2, Edit, Printer, Truck, Mail } from 'lucide-react';
import { format, type Locale } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { useOrders } from '@/hooks/useOrders';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { OrderPrintDialog } from './OrderPrintDialog';
import { EditOrderDialog } from './EditOrderDialog';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

interface OrderDetailsDialogProps {
  order: {
    id: string;
    order_number: string;
    pickup_address: string;
    delivery_address: string;
    pickup_date: string;
    delivery_date: string;
    status: string;
    proposed_price?: number;
    weight_kg: number;
    trailer_type: string;
    marketplace_visible: boolean;
    cargo_title?: string;
    cargo_description?: string;
    cargo_picture?: string | null;
    start_pictures?: string[] | null;
    delivery_pictures?: string[] | null;
    pickup_contact: string;
    pickup_phone: string;
    delivery_contact: string;
    delivery_phone: string;
    cargo_unit_type?: string;
    pallets_count?: number;
    volume_m3?: number;
    special_instructions?: string;
    start_details?: string;
    delivery_details?: string;
    qr_code?: string | null;
    pickup_city?: string | null;
    pickup_country?: string;
    delivery_city?: string | null;
    delivery_country?: string;
    transport_jobs?: Array<{
      id: string;
      driver_id: string | null;
      vehicle_id: string | null;
      booked_at: string | null;
      assigned_at: string | null;
      accepted_at: string | null;
      started_at: string | null;
      completed_at: string | null;
      companies: {
        name: string;
        email: string;
        phone: string;
        address: string;
        siret: string;
      };
      drivers: {
        license_number: string;
        status: string;
        users: {
          first_name: string;
          last_name: string;
          phone: string;
        };
      } | null;
      vehicles: {
        plate_number: string;
        brand: string;
        model: string;
        year: number;
        vehicle_types: {
          name: string;
          trailer_type: string;
        };
      } | null;
    }> | null;
  };
}

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-[#87ADD4]/10 text-[#5A6B7A]' },
  posted: { label: 'Publiée', color: 'bg-[#0880AE]/10 text-[#0880AE]' },
  booked: { label: 'Réservée', color: 'bg-[#F2AC57]/10 text-[#F2AC57]' },
  assigned: { label: 'Assignée', color: 'bg-[#37699E]/10 text-[#37699E]' },
  in_transit: { label: 'En transit', color: 'bg-[#FFBF07]/10 text-[#2D3748]' },
  delivered: { label: 'Livrée', color: 'bg-[#14A38B]/10 text-[#14A38B]' },
  cancelled: { label: 'Annulée', color: 'bg-[#FF7171]/10 text-[#FF7171]' },
};

export const OrderDetailsDialog = ({ order }: OrderDetailsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { deleteOrder } = useOrders();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const status = statusConfig[order.status as keyof typeof statusConfig];

  const localeMap: Record<string, Locale> = {
    fr: fr,
    en: enUS,
    es: es,
    de: de,
    it: it,
  };

  const currentLocale = localeMap[i18n.language] || fr;

  const handleDelete = async () => {
    try {
      await deleteOrder(order.id);
      toast.success(t('orders.details.deleteSuccess'));
      setDeleteDialogOpen(false);
      setOpen(false);
    } catch (error) {
      toast.error(t('orders.details.deleteError'));
    }
  };

  const handleEdit = () => {
    setOpen(false);
    navigate(`/shipper/orders/${order.id}/edit`);
  };

  const canDelete = order.status === 'draft' || order.status === 'posted';
  const canEdit = order.status === 'draft' || order.status === 'posted';

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 border-[#DBE2EA] text-[#0880AE] hover:bg-[#EBF4F8] hover:text-[#076A8E] font-['Funnel_Sans'] font-medium transition-colors duration-200"
          >
            {t('orders.card.details')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl">
          <DialogHeader>
            <div className="flex items-start gap-4">
              <div className="space-y-2 flex-1">
                <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {order.order_number}
                </DialogTitle>
                <Badge className={`${status.color} text-xs font-medium px-3 py-1.5 rounded-md`}>
                  {status.label}
                </Badge>
              </div>
              {order.qr_code && (
                <div className="bg-white p-2 rounded-lg border-2 border-[#37699E] flex-shrink-0 mr-8">
                  <QRCodeSVG value={order.qr_code} size={80} />
                </div>
              )}
            </div>
            <DialogDescription className="text-[#5A6B7A] font-['Funnel_Sans']">
              {t('orders.details.title')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Prix proposé */}
            {order.proposed_price && (
              <div className="flex items-baseline gap-2 pb-4 border-b border-[#DBE2EA]">
                <span className="text-4xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {order.proposed_price}€
                </span>
                <span className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                  {t('orders.details.proposedPrice')}
                </span>
              </div>
            )}

            {/* Marchandise */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                <Package size={20} className="text-[#0880AE]" />
                {t('orders.details.cargo')}
              </h3>
              <div className="space-y-2 pl-8">
                {order.cargo_title && (
                  <div>
                    <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{t('orders.details.cargoTitle')}</span>
                    <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">{order.cargo_title}</p>
                  </div>
                )}
                {order.cargo_description && (
                  <div>
                    <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{t('orders.details.cargoDescription')}</span>
                    <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">{order.cargo_description}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-[#2D3748] font-['Funnel_Sans']">
                  <div>
                    <span className="font-medium">{t('orders.details.weight')}</span> {order.weight_kg} kg
                  </div>
                  {order.cargo_unit_type && order.pallets_count && (
                    <div>
                      <span className="font-medium">
                        {order.cargo_unit_type === 'palettes' && t('orders.details.pallets')}
                        {order.cargo_unit_type === 'caisses' && t('orders.details.boxes')}
                        {order.cargo_unit_type === 'euro-palettes' && t('orders.details.euroPallets')}
                      </span>{' '}
                      {order.pallets_count}
                    </div>
                  )}
                  {order.volume_m3 && (
                    <div>
                      <span className="font-medium">{t('orders.details.volume')}</span> {order.volume_m3} m³
                    </div>
                  )}
                  <div>
                    <span className="font-medium">{t('orders.details.type')}</span> {order.trailer_type}
                  </div>
                </div>
              </div>
            </div>

            {/* Itinéraire */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                <MapPin size={20} className="text-[#0880AE]" />
                {t('orders.details.route')}
              </h3>
              <div className="space-y-4 pl-8">
                {/* Pickup */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-[#5A6B7A]" />
                    <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">
                      {t('orders.details.pickup')} {format(new Date(order.pickup_date), 'dd MMMM yyyy à HH:mm', { locale: currentLocale })}
                    </span>
                  </div>
                  <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans'] mb-1">{order.pickup_address}</p>
                  <div className="flex items-center gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {order.pickup_contact}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {order.pickup_phone}
                    </span>
                  </div>
                  {order.start_details && (
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] mt-1">{order.start_details}</p>
                  )}
                </div>

                {/* Delivery */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-[#5A6B7A]" />
                    <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">
                      {t('orders.details.delivery')} {format(new Date(order.delivery_date), 'dd MMMM yyyy à HH:mm', { locale: currentLocale })}
                    </span>
                  </div>
                  <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans'] mb-1">{order.delivery_address}</p>
                  <div className="flex items-center gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {order.delivery_contact}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {order.delivery_phone}
                    </span>
                  </div>
                  {order.delivery_details && (
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] mt-1">{order.delivery_details}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Images de la marchandise */}
            {order.cargo_picture && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca']">
                  {t('orders.details.cargoImage')}
                </h3>
                <img 
                  src={supabase.storage.from('order-images').getPublicUrl(order.cargo_picture).data.publicUrl}
                  alt="Marchandise"
                  className="w-full max-w-md rounded-xl border border-[#DBE2EA]"
                />
              </div>
            )}

            {/* Images au départ */}
            {order.start_pictures && order.start_pictures.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca']">
                  {t('orders.details.startImages')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {order.start_pictures.map((pic, idx) => (
                    <img 
                      key={idx}
                      src={supabase.storage.from('order-images').getPublicUrl(pic).data.publicUrl}
                      alt={`Départ ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-[#DBE2EA]"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Images à la livraison */}
            {order.delivery_pictures && order.delivery_pictures.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca']">
                  {t('orders.details.deliveryImages')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {order.delivery_pictures.map((pic, idx) => (
                    <img 
                      key={idx}
                      src={supabase.storage.from('order-images').getPublicUrl(pic).data.publicUrl}
                      alt={`Livraison ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-[#DBE2EA]"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Informations du transporteur */}
            {order.transport_jobs && order.transport_jobs.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                  <Truck size={20} className="text-[#0880AE]" />
                  {t('orders.details.transporterInfo')}
                </h3>
                
                {/* Entreprise */}
                <div className="space-y-2 pl-8">
                  <div>
                    <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{t('orders.details.company')} :</span>
                    <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">{order.transport_jobs[0].companies.name}</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                    <span className="flex items-center gap-1">
                      <Mail size={14} />
                      {order.transport_jobs[0].companies.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {order.transport_jobs[0].companies.phone}
                    </span>
                  </div>
                </div>

                {/* Chauffeur */}
                {order.transport_jobs[0].drivers && (
                  <div className="space-y-2 pl-8">
                    <div>
                      <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{t('orders.details.assignedDriver')} :</span>
                      <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                        {order.transport_jobs[0].drivers.users.first_name} {order.transport_jobs[0].drivers.users.last_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                      <span className="flex items-center gap-1">
                        <Phone size={14} />
                        {order.transport_jobs[0].drivers.users.phone}
                      </span>
                      <span>{t('orders.details.licenseNumber')} : {order.transport_jobs[0].drivers.license_number}</span>
                    </div>
                  </div>
                )}

                {/* Véhicule */}
                {order.transport_jobs[0].vehicles && (
                  <div className="space-y-2 pl-8">
                    <div>
                      <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{t('orders.details.vehicle')} :</span>
                      <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                        {order.transport_jobs[0].vehicles.brand} {order.transport_jobs[0].vehicles.model} ({order.transport_jobs[0].vehicles.year})
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                      <span>{t('orders.details.plateNumber')} : {order.transport_jobs[0].vehicles.plate_number}</span>
                      <span>{t('common.type')} : {order.transport_jobs[0].vehicles.vehicle_types.name}</span>
                      <span>{t('orders.details.trailerType')} : {order.transport_jobs[0].vehicles.vehicle_types.trailer_type}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Instructions spéciales */}
            {order.special_instructions && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca'] flex items-center gap-2">
                  <FileText size={20} className="text-[#0880AE]" />
                  {t('orders.details.specialInstructions')}
                </h3>
                <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans'] pl-8">{order.special_instructions}</p>
              </div>
            )}

            {/* Visibility */}
            {order.marketplace_visible && (
              <div className="text-sm text-[#0880AE] font-['Funnel_Sans'] pl-8">
                {t('orders.details.visibleMarketplace')}
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="flex items-center gap-2 bg-[#FFBF07] border-[#FFBF07] text-[#2D3748] hover:bg-[#E5AB06] hover:border-[#E5AB06] font-['Funnel_Sans'] font-semibold transition-colors duration-200"
              >
                <Edit size={16} />
                {t('orders.details.edit')}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setPrintDialogOpen(true)}
              className="flex items-center gap-2 border-[#DBE2EA] text-[#0880AE] hover:bg-[#EBF4F8] hover:text-[#076A8E] font-['Funnel_Sans'] font-medium transition-colors duration-200"
            >
              <Printer size={16} />
              {t('orders.details.print')}
            </Button>
            {canDelete && (
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                className="flex items-center gap-2 bg-[#FF7171] hover:bg-[#E65959] text-white font-['Funnel_Sans'] font-semibold transition-colors duration-200"
              >
                <Trash2 size={16} />
                {t('orders.details.delete')}
              </Button>
            )}
            <Button 
              onClick={() => setOpen(false)}
              className="bg-[#37699E] hover:bg-[#2E5782] text-white font-['Funnel_Sans'] font-semibold transition-colors duration-200"
            >
              {t('orders.details.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orders.details.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('orders.details.confirmDeleteText', { orderNumber: order.order_number })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('orders.details.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('orders.details.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OrderPrintDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        order={order}
      />

      <EditOrderDialog
        order={order}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </>
  );
};
